import * as _ from 'lodash'
import {
    ILock, ILockAcquireResponse,
    ILockObject,
    ILockOptions,
    ILockOwner,
    ILockPropertyChangeEvent,
    LockStatus
} from "./PA.Concurrency.IF.Lock";
import {BehaviorSubject} from "rxjs";
import {
    ILockServiceInternal,
    IServerLockAcquireReleaseResponse,
    IServerLockChannelMessage,
    IServerLockChannelMessageType
} from "./PA.Concurrency.IF.ServerLock";

const bDebug = false

export class Lock
    implements ILock
{
    private _classId: string
    private _key: string
    private _keyOld: string
    private _lockId: string | number
    private _object: ILockObject
    private _opts: ILockOptions

    private _onPropertyChanged: BehaviorSubject<ILockPropertyChangeEvent>
    private _owner: ILockOwner
    private _ownerTS: string
    private _selfOwner: ILockOwner
    private _status: LockStatus
    private _intLockClient: ILockServiceInternal
    private _bStatusOnly: boolean

    private _lastSequenceId: number
    private _lastMessage: IServerLockChannelMessage = undefined

    private _acquireResponse: IServerLockAcquireReleaseResponse = undefined
    private _releaseResponse: IServerLockAcquireReleaseResponse = undefined

    private _bLoaded: boolean
    private _onLoaded: Promise<void>

    constructor(sClassId: string, lockId: number | string, intLockClient: ILockServiceInternal, options?: ILockOptions) {
        let opts: ILockOptions =  {}
        _.defaults(opts, options)

        this._classId = sClassId
        this._lockId = lockId
        this._intLockClient = intLockClient
        this._opts = opts

        this._status = LockStatus.Initializing
        this._bStatusOnly = !!opts.bStatusOnly
        this._onPropertyChanged = new BehaviorSubject<ILockPropertyChangeEvent>(undefined)
        this._bLoaded = false
        this._onLoaded = undefined
        this._lastSequenceId = 0
        this._owner = {OwnerName: "", HostName: "", SessionId: ""}
        this._selfOwner =  {OwnerName: "", HostName: "", SessionId: ""}
        this._object = {UI_Id: opts.object?.UI_Id || "", Name: opts?.object?.Name || ""}



        // Setup lock on the server
        this.OnLoaded()
    }

    get ClassId(): string { return this._classId }
    get Key(): string { return this._key }
    get LockId(): string | number { return this._lockId }

    get Object(): ILockObject { return this._object }
    get OnPropertyChanged(): BehaviorSubject<ILockPropertyChangeEvent> { return this._onPropertyChanged }
    get bStatusOnly(): boolean { return this._bStatusOnly }
    get Owner(): ILockOwner { return this._owner }

    get OwnerCreateTS(): Date {
        if(_.isEmpty(this._ownerTS))
            return undefined

        let ts = Date.parse(this._ownerTS)
        if(_.isNaN(ts))
            return undefined
        return new Date(ts)
    }

    get Status(): LockStatus { return this._status }

    get bLoaded(): boolean { return this._bLoaded }
    OnLoaded(): Promise<void> {
        const self = this

        if(self._bLoaded)
            return Promise.resolve()

        if(self._onLoaded){  return self._onLoaded }

        //--
        //--  Setup channel for handling messages

        let returnValue = self._onLoaded = (async ()=> {
            //-- Subscribe Channels
            //
            let channel = await self._intLockClient.getLockMessageChannel(this._classId, this._lockId);
            if(!channel.isSubscribed(true))
                channel.subscribe()

            if(bDebug)
                console.log("Lock setting up listener on Channel: ", channel?.name);

            (async ()=> {
                for await (let msg of channel){
                    if(bDebug)
                        console.log("Channel Message Received: ", msg)

                    self.processChannelMessage(msg)

                    if(self._status == LockStatus.Disconnected) {
                        if(bDebug)
                            console.log("Disconnected: stop lock eventhandling on channel: ", channel.name)
                        return // stop event handling
                    }
                }
            })();

            //-- Call Server to create Lock
            //
            let serverLock = await self._intLockClient.createServerLock(self._classId, self._lockId, this._opts)
            if(!serverLock){
                throw new Error("Unable to create server lock")
            }

            self._key = serverLock.LockKey
            //-- Race condition of channel message and receiving lock key, so process last channel message
            if(!_.isEmpty(self._lastMessage))
                self.processChannelMessage(self._lastMessage, true)

            if(self._opts.bTryAcquire){
                self.acquire()
            }

            if(bDebug)
                console.log("Lock.OnLoaded Completed")
        })();

        return returnValue
    }

    private processChannelMessage(msg:  IServerLockChannelMessage, pForce?: boolean){
        const self = this

        if(self._status == LockStatus.Disconnected){ return }

        let bForceProcessing = !!pForce
        if(!_.isNumber(msg.MessageSequenceId)){ return }

        if(msg.MessageSequenceId > self._lastSequenceId) {
            self._lastSequenceId = msg.MessageSequenceId
            self._lastMessage = msg
        }
        if((!bForceProcessing) && (msg.MessageSequenceId != self._lastSequenceId)) {
            if(bDebug)
                console.log(`Message skipped (${self._key}): `, msg)
            return
        }

        //-- If I'm not initialized, ignore message
        if(_.isEmpty(self._key)) {
            if(bDebug)
                console.log(`Message skipped (empty key): `, msg)
            return
        }

        switch (msg.MessageType){

            case IServerLockChannelMessageType.ClassUpdate:
                let bSelfOwned = (msg?.OwnerLock?.LockKey == self._key)
                let bUnlocked = _.isEmpty(msg.OwnerLock)
                let ownerLock = msg.OwnerLock

                if (bUnlocked) {
                    self._status = LockStatus.Unclaimed
                    self._owner = {OwnerName: "-", HostName: "-", SessionId: "-"}
                    self._ownerTS = ""
                } else {
                    if (bSelfOwned) {
                        self._status = LockStatus.Locked
                        self._owner = _.clone(self._selfOwner)
                        self._ownerTS = ownerLock.CreationTS
                    }
                    else {
                        self._status = LockStatus.ReadOnly
                        self._owner.OwnerName = ownerLock?.OwnerName || "Unknown"
                        self._owner.HostName = ownerLock?.HostName || "Unknown"
                        self._owner.SessionId = ownerLock?.SessionId || "Unknown"
                        self._ownerTS = ownerLock.CreationTS
                    }
                    self._object = self._object || {UI_Id: "Not Set", Name: "Not Set"}
                    let object = msg.Object
                    if(!_.isEmpty(object)){
                        self._object.UI_Id = object.UI_Id
                        self._object.Name = object.Name
                    }
                }

                self.notify("Status", self.Status)
                self.notify('Owner', self.Owner)
                self.notify('OwnerCreateTS', self.OwnerCreateTS)
                break;

            case IServerLockChannelMessageType.Destroy:
                let msgLockKey = msg?.LockKey
                if(!_.isEmpty(msgLockKey) &&  msgLockKey == (self._key || self._keyOld)){
                    self._status = LockStatus.Disconnected
                    self._owner = {OwnerName: "-", HostName: "-", SessionId: "-"}
                    self._ownerTS = ""
                    self.notify("Status", self._status)
                }
                break;

        }

    }

    private notify(propertyName: string, value: any): void {
        const self = this
        self._onPropertyChanged.next({
            PropertyChanged: propertyName,
            PropertyValue: value,
            Lock: self
        })
    }

    async acquire(sTimeoutSeconds?: number): Promise<ILockAcquireResponse> {
        const self = this

        if(_.isEmpty(self._key)){ return {bSuccess: false, sFailureReason: "Empty Key"} }
        if(self._bStatusOnly){ return {bSuccess: false, sFailureReason: "Status Only Lock"} }

        let resp =self._acquireResponse = await self._intLockClient.acquireServerLock(self._key, sTimeoutSeconds)

        //-- The status update should be made through the channel
        if(bDebug)
            console.log(resp)

        return {bSuccess: resp.bSuccess, sFailureReason: resp.sFailureReason || ""}
    }

    async destroy(sTimeoutSeconds?: number): Promise<void> {
        const self = this

        if(_.isEmpty(self._key)){ return }
        if(self._bStatusOnly){ return }

        await self._intLockClient.destroyServerLock(self._key, sTimeoutSeconds)
        self._keyOld = self._key
        self._status = LockStatus.Disconnected

        //-- The status update should be made through the channel
        if(bDebug)
            console.log(`ServerLock destroyed: ${self._keyOld}`)

        return

    }

    async release(sTimeoutSeconds?: number): Promise<void> {
        const self = this

        if(_.isEmpty(self._key)){ return }
        if(self._bStatusOnly){ return }

        let resp =self._releaseResponse = await self._intLockClient.releaseServerLock(self._key, sTimeoutSeconds)

        //-- The status update should be made through the channel
        if(bDebug)
            console.log(resp)

        return
    }

}